/**
 * @GestionalePD Admin campaign UI
 */
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import { useNavigate } from 'react-router';
import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import {
  Card,
  Stack,
  Container,
  Typography,
  Grid,
  Table,
  TableHead,
  TableBody,
  TableContainer,
  TableFooter,
  TablePagination,
  TableCell,
  TableRow,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
} from '@mui/material';
import { styled } from '@mui/system';
import useMediaQuery from '@mui/material/useMediaQuery';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Unstable_Popup as BasePopup } from '@mui/base/Unstable_Popup';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import AddIcon from '@mui/icons-material/Add';
//----------------------------------------------------------------------------------
import {
  PopAnimation,
  PopupSuccessBody,
  PopupErrorBody,
  POPUP_DURATION_SUCCESS,
  POPUP_DURATION_ERROR,
} from '../../utils/popUpStyles';
import { DefaultOutlinedButton, DefaultContainedButton, CustomIconButton } from '../../utils/Buttons';
import { CustomSnackbar, CreateSnackbarFunctions } from '../../utils/Snackbar';
import { CustomTextField, CustomDatePicker } from '../../utils/StyledInputs';
import { ErrorUI, HttpErrorHandler } from '../../utils/ErrorHandler';
import { PaginationAPI, theme } from '../../utils/TableStyles';
import { LoadingUI } from '../../utils/LayoutStyles';
import { CommonLabel } from '../../utils/Common';
import API from '../../api';

/**
 * @returns Admin Campaign page UI
 */

// custom popup style
const Popup = styled(BasePopup)`
  z-index: 1400;
`;

dayjs.locale('it');

export default function Campaigns() {
  // general
  const is600Screen = useMediaQuery('(min-width: 600px)');
  const navigate = useNavigate();
  const [response, setResponse] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [currentProfile, setCurrentProfile] = useState('');

  // popup handler
  const [anchor, setAnchor] = useState(null);
  const [open, setOpen] = useState(false);
  const [popUpText, setPopUpText] = useState('');
  const [popupSuccess, setPopupSuccess] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState('noDialog');
  const showPopup = (success, text) => {
    setPopupSuccess(success);
    setOpen(true);

    if (text) {
      setPopUpText(text);
    }

    setTimeout(
      () => {
        setOpen(false);
      },
      success ? POPUP_DURATION_SUCCESS : POPUP_DURATION_ERROR
    );
  };

  // popup component
  const displayPopup = () => {
    return (
      <Container ref={setAnchor} sx={{ position: 'absolute', bottom: '-20px' }}>
        <Popup anchor={anchor} open={open}>
          {popupSuccess ? (
            <PopupSuccessBody sx={{ whiteSpace: 'pre-line' }}>{popUpText}</PopupSuccessBody>
          ) : (
            <PopupErrorBody>{popUpText}</PopupErrorBody>
          )}
        </Popup>
      </Container>
    );
  };

  // tab handler
  const [filterValue, setFilterValue] = useState(0);
  const handleChangeFilterTab = (event, newValue) => setFilterValue(newValue);

  // campaign handler
  const [campaigns, setCampaigns] = useState([]);
  const [apiResponseData, setApiResponseData] = useState({});
  const [activeCampaigns, setActiveCampaigns] = useState([]);
  const [inactiveCampaigns, setInactiveCampaigns] = useState([]);
  const [displayedCampaigns, setDisplayedCampaigns] = useState([]);
  const [canCreateCampaign, setCanCreateCampaign] = useState(false);

  // new campaign handler
  const emptyCampaign = {
    start_date: '',
    end_date: '',
    start_registration_date: '',
    end_registration_date: '',
  };
  const [openNewCampaign, setOpenNewCampaign] = useState(false);
  const [campaignActive, setCampaignActive] = useState('');
  const [newCampaignDates, setNewCampaignDates] = useState(emptyCampaign);
  const [newCampaignPrefix, setNewCampaignPrefix] = useState(null);

  // open new campaign dialog handler
  const handleOpenNewCampaign = () => {
    setIsDialogOpen('newCampaign');
    setOpenNewCampaign(true);
  };

  // close new campaign dialog handler
  const handleCloseNewCampaign = () => {
    setOpenNewCampaign(false);
    setCampaignActive('');
    setIsDialogOpen('noDialog');
    setOpen(false);
  };

  // table pagination handler
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(is600Screen ? 10 : 5);
  const [rowsApiCalled, setRowsApiCalled] = useState(false);
  const [sliceStartDisplay, setSliceStartDisplay] = useState(0);
  const [sliceEndDisplay, setSliceEndDisplay] = useState(0);
  const emptyRows =
    page > 0
      ? rowsPerPage === 5 && page % 2 === 0
        ? Math.max(0, 1 * rowsPerPage - displayedCampaigns.length)
        : rowsPerPage === 5 && page % 2 !== 0
        ? Math.max(0, 1 * rowsPerPage + 5 - displayedCampaigns.length)
        : rowsPerPage === 10
        ? Math.max(0, 1 * rowsPerPage - displayedCampaigns.length)
        : 0
      : 0;

  // campaign table real time pagination handler
  useEffect(() => {
    if (rowsPerPage === 10) {
      setSliceStartDisplay(0);
      setSliceEndDisplay(10);
    } else if (rowsPerPage === 5) {
      if (page % 2 === 0) {
        setSliceStartDisplay(0);
        setSliceEndDisplay(5);
      } else {
        setSliceStartDisplay(5);
        setSliceEndDisplay(10);
      }
    }
  }, [rowsPerPage, page]);

  // page change handler
  const handleChangePage = (event, newPage) => setPage(newPage);

  // change rows per page handler
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);

    if (filterValue === 0) {
      getCampaigns();
    } else if (filterValue === 1) {
      getActiveCampaigns(true);
    } else {
      getInactiveCampaigns(true);
    }

    setRowsApiCalled(false);
  };

  // Snackbar handler
  const {
    open: openNewCampaignSnack,
    handleOpen: handleOpenNewCampaignSnack,
    handleClose: handleCloseNewCampaignSnack,
  } = CreateSnackbarFunctions(false);

  useEffect(() => {
    if (Object.keys(apiResponseData).length > 0) {
      setDisplayedCampaigns(apiResponseData.data);
    }
  }, [apiResponseData]);

  // Change Displayed Campaigns Data
  useEffect(() => {
    setPage(0);

    switch (filterValue) {
      case 0:
        setApiResponseData(campaigns);
        setDisplayedCampaigns(campaigns.data);
        break;
      case 1:
        setApiResponseData(activeCampaigns);
        setDisplayedCampaigns(activeCampaigns.data);
        break;
      case 2:
        setApiResponseData(inactiveCampaigns);
        setDisplayedCampaigns(inactiveCampaigns.data);
        break;
      default:
        break;
    }
  }, [filterValue]);

  // campaign table status button color
  const getCampaignTableStatusButtonColor = (active) => {
    const numericActive = Number(active);

    switch (numericActive) {
      case 0:
        return '#b71d18';
      case 1:
        return '#118d57';
      default:
        return '#b71d18';
    }
  };

  // campaign table status button background color
  const getCampaignTableStatusButtonBGColor = (active) => {
    const numericActive = Number(active);

    switch (numericActive) {
      case 0:
        return '#f7ddd8';
      case 1:
        return '#d3efdf';
      default:
        return '#f7ddd8';
    }
  };

  // campaign table status
  const getCampaignTableStatus = (active) => {
    const numericActive = Number(active);
    switch (numericActive) {
      case 0:
        return 'Non Attiva';
      case 1:
        return 'Attiva';
      default:
        return 'Non Attiva';
    }
  };

  // create new campaign request
  const createNewCampaign = (e) => {
    e.preventDefault();

    // new campaign date formatting
    const startDate = dayjs(newCampaignDates.start_date).format('YYYY/MM/DD');
    const endDate = dayjs(newCampaignDates.end_date).format('YYYY/MM/DD');
    const startRegistrationDate = dayjs(newCampaignDates.start_registration_date).format('YYYY/MM/DD');
    const endRegistrationDate = dayjs(newCampaignDates.end_registration_date).format('YYYY/MM/DD');

    // campaign form data
    const formData = new FormData(e.currentTarget);

    // campaign data object creation
    const campaignData = {
      active: campaignActive === 'active' ? 1 : 0,
      description: formData.get('description'),
      end_date: endDate,
      end_registrations_date: endRegistrationDate,
      start_date: startDate,
      start_registrations_date: startRegistrationDate,
      prefix: newCampaignPrefix,
    };

    let itemsLeft = false;

    // camapign data verification
    Object.keys(campaignData).forEach((key) => {
      const value = campaignData[key];

      if (Object.prototype.hasOwnProperty.call(campaignData, key)) {
        if (
          value === undefined ||
          value === null ||
          value === 'Invalid Date' ||
          (typeof value === 'string' && value.length === 0)
        ) {
          itemsLeft = true;
          handleOpenNewCampaignSnack();
        }
      }
    });

    if (!itemsLeft) {
      if (dayjs(campaignData.end_date).isBefore(dayjs(campaignData.start_date))) {
        showPopup(false, 'La data di fine non può essere antecedente a quella di inizio');
      } else if (dayjs(campaignData.end_registrations_date).isBefore(dayjs(campaignData.start_registrations_date))) {
        showPopup(false, 'La data di fine delle registrazioni non può essere antecedente a quella di inizio');
      } else {
        API.post('/campaigns', campaignData)
          .then((response) => {
            // create campaign success operation
            handleCloseNewCampaign();
            setNewCampaignDates(emptyCampaign);
            getCampaigns();

            if (campaignData.active === 1) {
              getActiveCampaigns();
            } else {
              getInactiveCampaigns();
            }
          })
          .catch((error) => {
            HttpErrorHandler(error, setPopUpText);
            showPopup(false);
          });
      }
    }
  };

  // get active campaigns request
  const getActiveCampaigns = (display) => {
    API.get('/campaigns/status?val=active').then((response) => {
      setRowsApiCalled(true);
      setActiveCampaigns(response.data);
      if (display) setDisplayedCampaigns(response.data.data);
    });
  };

  const getInactiveCampaigns = (display) => {
    API.get('/campaigns/status?val=inactive').then((response) => {
      setRowsApiCalled(true);
      setInactiveCampaigns(response.data);
      if (display) setDisplayedCampaigns(response.data.data);
    });
  };

  // get campaigns request
  const getCampaigns = () => {
    setErrorMessage(null);

    API.get('/campaigns')
      .then((response) => {
        setCampaigns(response.data);
        setDisplayedCampaigns(response.data.data);
        setApiResponseData(response.data);
        setCanCreateCampaign(response.data.user_auth === 'W');
        setRowsApiCalled(true);
      })
      .catch((error) => {
        HttpErrorHandler(error, setErrorMessage);
      })
      .finally(() => setResponse(true));
  };

  // initial campaign data loading
  useEffect(() => {
    getActiveCampaigns(false);
    getInactiveCampaigns(false);
    getCampaigns();
  }, []);

  // get user data
  useEffect(() => {
    let data;

    if (localStorage.getItem('userData') !== 'undefined') data = JSON.parse(localStorage.getItem('userData'));

    if (data) {
      const _currentProfile = data.roles.filter((role) => role.last_used);
      setCurrentProfile(_currentProfile);
    }
  }, []);

  if (currentProfile && currentProfile[0].prof_type_name !== 'ADMIN') return <Navigate to={{ pathname: '/' }} />;

  return (
    <div>
      {/* page header title */}
      <Helmet>
        <title>Campagne</title>
      </Helmet>
      <Container maxWidth="xl">
        <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={{ paddingBottom: '24px' }}>
          {response && (
            // create campaign action button
            <DefaultContainedButton
              onClick={handleOpenNewCampaign}
              customStyles={{ display: canCreateCampaign ? 'flex' : 'none' }}
              startIcon={<AddIcon />}
            >
              Crea Campagna
            </DefaultContainedButton>
          )}
        </Stack>

        {!response ? (
          // loading spinner UI
          LoadingUI()
        ) : !errorMessage ? (
          <Card>
            {/* campaign page tab filters */}
            <Tabs
              value={filterValue}
              onChange={handleChangeFilterTab}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              textColor="secondary"
              indicatorColor="secondary"
              sx={{ boxShadow: 'rgba(145, 158, 171, 0.08) 0px -2px 0px 0px inset', padding: '0px 24px 0' }}
            >
              {/* all campaigns tab */}
              <Tab
                disableRipple
                icon={
                  <Typography
                    sx={{
                      backgroundColor: filterValue === 0 ? '#000000' : '#edeff1',
                      borderRadius: '6px',
                      color: filterValue === 0 ? '#ffffff' : '#637381',
                      padding: '2px 8px',
                      fontSize: '1em',
                      fontWeight: 'bold',
                    }}
                  >
                    {campaigns.total}
                  </Typography>
                }
                iconPosition="end"
                label="Tutte"
              />

              {/* active campaigns tab */}
              <Tab
                disableRipple
                icon={
                  <Typography
                    sx={{
                      backgroundColor: filterValue === 1 ? '#22c55e' : '#dbf6e5',
                      borderRadius: '6px',
                      color: filterValue === 1 ? '#dbf6e5' : '#118d57',
                      padding: '2px 8px',
                      fontSize: '1em',
                      fontWeight: 'bold',
                    }}
                  >
                    {activeCampaigns.total}
                  </Typography>
                }
                iconPosition="end"
                label="Attive"
              />

              {/* close campaigns tab */}
              <Tab
                disableRipple
                icon={
                  <Typography
                    sx={{
                      backgroundColor: filterValue === 2 ? '#ff5630' : '#ffe4de',
                      borderRadius: '6px',
                      color: filterValue === 2 ? '#ffffff' : '#b71d18',
                      padding: '2px 8px',
                      fontSize: '1em',
                      fontWeight: 'bold',
                    }}
                  >
                    {inactiveCampaigns.total}
                  </Typography>
                }
                iconPosition="end"
                label="Terminate"
              />
            </Tabs>

            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ paddingTop: '24px' }}>
              {/* create new campaign dialog */}
              <Dialog fullWidth open={openNewCampaign} onClose={() => {}}>
                {/* create new campaign dialog title */}
                <DialogTitle sx={{ textAlign: 'center' }}>Nuova Campagna</DialogTitle>
                <form onSubmit={createNewCampaign}>
                  {/* create new campaign dialog content */}
                  <DialogContent>
                    <Grid container spacing={3}>
                      {/* campaign name */}
                      <Grid item xs={12}>
                        <CustomTextField required name="description" label="Nome Campagna" />
                      </Grid>

                      {/* campaign start date */}
                      <Grid item xs={12} md={6}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                          <CustomDatePicker
                            required
                            label="Data Inizio *"
                            maxDate={dayjs(newCampaignDates.end_date)}
                            onChange={(date) => {
                              setNewCampaignDates({
                                ...newCampaignDates,
                                start_date: date,
                              });
                            }}
                          />
                        </LocalizationProvider>
                      </Grid>

                      {/* campaign end date */}
                      <Grid item xs={12} md={6}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                          <CustomDatePicker
                            required
                            label="Data Fine *"
                            minDate={dayjs(newCampaignDates.start_date)}
                            onChange={(date) => {
                              setNewCampaignDates({
                                ...newCampaignDates,
                                end_date: date,
                              });
                            }}
                          />
                        </LocalizationProvider>
                      </Grid>

                      {/* campaign registration start date */}
                      <Grid item xs={12} md={6}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                          <CustomDatePicker
                            required
                            label="Data Inizio Registrazioni *"
                            maxDate={dayjs(newCampaignDates.end_registration_date)}
                            onChange={(date) => {
                              setNewCampaignDates({
                                ...newCampaignDates,
                                start_registration_date: date,
                              });
                            }}
                          />
                        </LocalizationProvider>
                      </Grid>

                      {/* campaign registration end date */}
                      <Grid item xs={12} md={6}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                          <CustomDatePicker
                            required
                            label="Data Fine Registrazioni *"
                            minDate={dayjs(newCampaignDates.start_registration_date)}
                            onChange={(date) => {
                              setNewCampaignDates({
                                ...newCampaignDates,
                                end_registration_date: date,
                              });
                            }}
                          />
                        </LocalizationProvider>
                      </Grid>

                      {/* campaign name */}
                      <Grid item xs={12} md={6}>
                        <CustomTextField
                          required
                          name="prefisso"
                          onKeyDown={(e) => {
                            if (e.key !== 'Backspace' && e.key !== 'Tab' && !/[0-9\b]/.test(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          pattern="[0-9]{12}"
                          inputProps={{ maxLength: 4 }}
                          label="Prefisso"
                          onChange={(e) => setNewCampaignPrefix(e.currentTarget.value)}
                        />
                      </Grid>

                      {/* campaign status */}
                      <Grid item xs={12} md={6}>
                        <FormControl color="secondary" sx={{ width: '100%' }}>
                          <InputLabel>Stato Campagna *</InputLabel>
                          <Select
                            sx={{ width: '100%' }}
                            value={campaignActive}
                            label="Stato Campagna *"
                            onChange={(e) => setCampaignActive(e.target.value)}
                          >
                            <MenuItem value="active">Attiva</MenuItem>
                            <MenuItem value="inactive">Non Attiva</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>

                    {isDialogOpen === 'newCampaign' && displayPopup()}
                  </DialogContent>

                  {/* campaign dialog action buttons */}
                  <DialogActions sx={{ paddingBottom: 3, paddingRight: 3 }}>
                    <DefaultOutlinedButton onClick={handleCloseNewCampaign}>Chiudi</DefaultOutlinedButton>
                    <DefaultContainedButton type="submit">Crea</DefaultContainedButton>
                    <CustomSnackbar open={openNewCampaignSnack} handleClose={handleCloseNewCampaignSnack} />
                  </DialogActions>
                </form>
              </Dialog>
            </Stack>

            {/* campaign list table */}
            <TableContainer sx={{ padding: '0 24px 6px' }}>
              <Table size="small">
                {/* campaign table header */}
                <TableHead>
                  <TableRow>
                    <TableCell>Nome Campagna</TableCell>
                    <TableCell>Data Inizio</TableCell>
                    <TableCell>Data Fine</TableCell>
                    <TableCell>Stato Campagna</TableCell>
                    {canCreateCampaign && <TableCell />}
                  </TableRow>
                </TableHead>

                {/* campaign table body */}
                {displayedCampaigns && displayedCampaigns.length > 0 ? (
                  <TableBody>
                    {rowsApiCalled ? (
                      displayedCampaigns.slice(sliceStartDisplay, sliceEndDisplay).map((campaign) => (
                        <TableRow hover key={campaign.id}>
                          <TableCell>{campaign.description}</TableCell>
                          <TableCell>{dayjs(campaign.start_date).format('DD/MM/YYYY')}</TableCell>
                          <TableCell>{dayjs(campaign.end_date).format('DD/MM/YYYY')}</TableCell>
                          <TableCell>
                            <CommonLabel
                              textColor={getCampaignTableStatusButtonColor(campaign.active)}
                              bgColor={getCampaignTableStatusButtonBGColor(campaign.active)}
                              text={getCampaignTableStatus(campaign.active)}
                            />
                          </TableCell>
                          {canCreateCampaign && (
                            <TableCell align="right">
                              <CustomIconButton
                                size="small"
                                onClick={() =>
                                  navigate('/admin/campaigns/campaign-details', { state: { campaignID: campaign.id } })
                                }
                              >
                                <KeyboardArrowRightIcon />
                              </CustomIconButton>
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    ) : (
                      // loading spinner UI
                      <TableRow>
                        <TableCell colSpan={9}>{LoadingUI()}</TableCell>
                      </TableRow>
                    )}

                    {/* empty table rows */}
                    {emptyRows > 0 && (
                      <TableRow style={{ height: 47 * emptyRows }}>
                        <TableCell colSpan={5} />
                      </TableRow>
                    )}
                  </TableBody>
                ) : (
                  // campaign empty table body
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ textAlign: 'center' }} colSpan={5}>
                        Nessuna Campagna Disponibile!
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}

                {/* table footer pagination */}
                <TableFooter>
                  <TableRow>
                    <ThemeProvider theme={theme}>
                      <TablePagination
                        sx={{ borderBottom: 'none' }}
                        rowsPerPageOptions={[]}
                        colSpan={5}
                        count={apiResponseData.total}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelRowsPerPage="Righe per Pagina"
                        ActionsComponent={(props) => (
                          <PaginationAPI
                            {...props}
                            elements={10}
                            apiResponseData={apiResponseData}
                            setApiResponseData={setApiResponseData}
                            lastPage={apiResponseData.last_page}
                          />
                        )}
                      />
                    </ThemeProvider>
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </Card>
        ) : (
          // error message UI
          ErrorUI(errorMessage)
        )}
      </Container>
    </div>
  );
}
